import type { ChipProps } from "@mantine/core";
import { Chip } from "@mantine/core";
import type { CSSProperties } from "react";

interface FilterChipProps extends Omit<ChipProps, "styles"> {
  /**
   * Whether the chip is in active/checked state
   */
  isActive: boolean;
  /**
   * Primary color for the chip (used for border and active background)
   */
  color: string;
  /**
   * Optional icon or avatar to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Border width in pixels
   * @default 2
   */
  borderWidth?: number;
  /**
   * Custom styles to merge with base styles
   */
  customStyles?: {
    label?: CSSProperties;
  };
}

/**
 * Reusable filter chip component with consistent styling.
 * Used across Status, Assignee, and Tax Year filters.
 */
const FilterChip = ({
  isActive,
  color,
  icon,
  borderWidth = 2,
  customStyles = {},
  children,
  disabled,
  ...chipProps
}: FilterChipProps) => {
  const baseStyles: CSSProperties = {
    backgroundColor: disabled ? "#f5f5f5" : isActive ? color : "white",
    color: disabled
      ? "#9ca3af"
      : isActive
        ? customStyles.label?.color || "white"
        : customStyles.label?.color || "#333",
    border: `${borderWidth}px solid ${disabled ? "#e5e7eb" : color}`,
    paddingLeft: icon ? "10px" : "14px",
    paddingRight: "14px",
    paddingTop: "10px",
    paddingBottom: "10px",
    fontWeight: 500,
    fontSize: "15px",
    lineHeight: "1.2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "0px",
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    ...customStyles.label,
  };

  const hoverStyles = disabled
    ? {}
    : {
        backgroundColor: isActive ? color : "#f0f0f0",
        filter: isActive ? "brightness(0.9)" : "none",
      };

  return (
    <Chip
      {...chipProps}
      checked={isActive}
      disabled={disabled}
      variant="filled"
      style={
        {
          margin: 0,
          padding: 0,
          "--chip-padding": "0px",
          "--chip-checked-padding": "0px",
        } as React.CSSProperties
      }
      styles={{
        input: {
          margin: 0,
          padding: 0,
        },
        label: {
          margin: 0,
          height: "100%",
          ...baseStyles,
          "&:hover": hoverStyles,
        },
      }}
    >
      {icon || children ? (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {icon}
          {children}
        </span>
      ) : (
        children
      )}
    </Chip>
  );
};

export default FilterChip;
